import React, { Children, isValidElement } from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import { useMerchandisingHeadlineAndDescription } from 'app/shared/utils/useMerchandisingHeadlineAndDescription';
import { AttendeeState, Event } from 'app/shared/typings';
import { Box, Typography } from 'app/shared/components/atoms';
import { useMoreLocalEvents } from 'app/fan/utils/useMoreLocalEvents';
import { FullWidthColumn } from 'app/fan/components/templates/BasePageGrid';
import {
  EventCharacteristicsList,
  EventHeaderImage,
  EventInfo,
  EventPageNewsletterForm,
  FacebookMetaMicrodataTags,
  RecommendedEvents,
} from 'app/fan/components/templates/EventPage/components';

import {
  EventPageDesktopSecondColumn,
  EventPageFirstColumn,
  EventPageGrid,
} from './EventPageGrid';

export { EventPageDesktopSecondColumn, EventPageFirstColumn, EventPageGrid };

function findReactChildByQaId(children: React.ReactNode, qaId: string) {
  return Children.toArray(children).find(
    (child) => isValidElement(child) && child.props['data-qaid'] === qaId
  );
}

function findReactChildrenNotMatchingQaId(
  children: React.ReactNode,
  qaId: string
) {
  return Children.toArray(children).filter(
    (child) => !isValidElement(child) || child.props['data-qaid'] !== qaId
  );
}

interface Props {
  'data-qaid'?: string;
  event: Event;
  isOutOfStock?: boolean;
  ticketSaleStatus?: string;
  attendeeState?: AttendeeState;
  numTickets?: number;
  secondColumn: React.ReactNode;
  children?: React.ReactNode;
}

export const EventPageTemplate = ({
  'data-qaid': qaId,
  event,
  isOutOfStock,
  ticketSaleStatus,
  secondColumn,
  children,
  ...props
}: Props) => {
  const theme = useCurrentTheme();
  const moreLocalEvents = useMoreLocalEvents();
  const aboutThisConcert = useMerchandisingHeadlineAndDescription({ event });

  const firstColumnQaId = 'event-page-first-column-children';
  const firstColumnChildren = findReactChildByQaId(children, firstColumnQaId);
  const otherChildren = findReactChildrenNotMatchingQaId(
    children,
    firstColumnQaId
  );

  return (
    <EventPageGrid data-qaid={qaId}>
      {!event.competition && (
        <FacebookMetaMicrodataTags
          event={event}
          isOutOfStock={!!isOutOfStock}
        />
      )}
      <EventPageFirstColumn>
        {aboutThisConcert.title && (
          <Box
            pb={4}
            pt={[4, 0, 0, 0]}
            px={theme.dimensions.horizontalPadding.pageGridItem}
          >
            <Typography
              fontSize="fontSize600"
              fontFamily="title"
              fontWeight="medium"
              color={theme.colors.primary.white.base}
            >
              {aboutThisConcert.title}
            </Typography>
          </Box>
        )}
        <EventHeaderImage event={event} {...props} />
        {firstColumnChildren}
        <EventInfo event={event} />
        <EventCharacteristicsList event={event} />
      </EventPageFirstColumn>
      <EventPageDesktopSecondColumn px={[0, 0, 4, 6]} py={[0, 0, 8, 8]}>
        {secondColumn}
      </EventPageDesktopSecondColumn>
      <FullWidthColumn>
        <RecommendedEvents
          event={event}
          recommendedEvents={moreLocalEvents}
          ticketSaleStatus={ticketSaleStatus}
        />
      </FullWidthColumn>
      <EventPageNewsletterForm />
      {otherChildren}
    </EventPageGrid>
  );
};
